import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

/**
 * OfflineService (Angular 18) — B2C checkout point-of-sale offline
 * transactions API.
 *
 * Replaces apps/b2b-ui/common/services/api/offline/offline.service.js.
 *
 * Endpoint base hardcoded to /v2/api/b2c/checkout/pointofsale/offline (legacy
 * provider's setEndpoint() value).
 *
 * Response shape — IMPORTANT
 * --------------------------
 * Unlike most migrated API services that return `Promise<AjsResponse<T>>`
 * (i.e. `{ data: T }`), legacy `OfflineService` returns the UNWRAPPED payload
 * via `.then(UtilsService.unwrapAcmeResponse)`. The 6 legacy consumers
 * (OfflineListController, OfflineDetailController, OfflineOrderDetailController)
 * read the response directly without `.data` indirection.
 *
 * The Angular 18 port preserves that exact shape: every method returns
 * `Promise<T>` (the unwrapped payload), NOT `Promise<AjsResponse<T>>`.
 *
 * See:
 *   apps/b2b-frontend/app/core/utils/utils.service.ts → unwrapAcmeResponse
 *   ({ data: { payload: T } }) → T
 */
const BASE = '/v2/api/b2c/checkout/pointofsale/offline';

export interface OfflineSearchQuery {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  /**
   * Transaction status filter. Lower-cased before being sent; empty / falsy
   * values are sent as `null` for legacy parity.
   */
  status?: string;
}

@Injectable({ providedIn: 'root' })
export class OfflineService {
  private readonly http = inject(HttpClient);

  /**
   * Unwraps `{ payload: T }` (the body that `$http.get(...).then(unwrapAcmeResponse)`
   * exposed in the legacy service). HttpClient already strips the outer
   * `{ data }` wrapper, so we only need to read `.payload`.
   */
  private unwrap<T = unknown>() {
    return map((body: { payload: T }) => body.payload);
  }

  find(uuid: string): Promise<unknown> {
    return firstValueFrom(this.http.get<{ payload: unknown }>(`${BASE}/${uuid}`).pipe(this.unwrap()));
  }

  archive(uuid: string, reason: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post<{ payload: unknown }>(`${BASE}/${uuid}/archive`, { reason }).pipe(this.unwrap()),
    );
  }

  list(query?: OfflineSearchQuery): Promise<unknown> {
    const q = query ?? {};
    const statusLower = (q.status ?? '').toLowerCase();
    return firstValueFrom(
      this.http
        .get<{ payload: unknown }>(BASE, {
          params: {
            page: q.page ?? '',
            pageSize: q.pageSize ?? '',
            sortField: q.sortField ?? '',
            sortDirection: q.sortDirection ?? '',
            status: statusLower || '',
          },
        })
        .pipe(this.unwrap()),
    );
  }

  rerun(uuidList?: string | string[]): Promise<unknown> {
    const list = uuidList == null ? [] : ([] as string[]).concat(uuidList);
    const obs = list.length
      ? this.http.post<{ payload: unknown }>(`${BASE}/rerun`, list)
      : this.http.post<{ payload: unknown }>(`${BASE}/rerun/failed`, null);
    return firstValueFrom(obs.pipe(this.unwrap()));
  }

  executions(uuid: string): Promise<unknown> {
    return firstValueFrom(this.http.get<{ payload: unknown }>(`${BASE}/${uuid}/executions`).pipe(this.unwrap()));
  }

  details(uuid: string): Promise<unknown> {
    return firstValueFrom(this.http.get<{ payload: unknown }>(`${BASE}/${uuid}/details`).pipe(this.unwrap()));
  }
}
